import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useIsFetching } from '@tanstack/react-query';
import { I18N_KEYS } from '@/language/keys';
import { getImportedModelNodeInfo } from '@/utils/model-import.utils';
import { useIsCustomModelApprovalEnabled } from '@/queries/useModelManagement';
import { MODEL_APPROVAL_STATUSES_QUERY_KEY } from '@/queries/useModelApproval';
import useWorkspacesStore from '@/state/workspaces.state';
import { useWorkflowStore } from '@/state/workflow.state';
import { WorkspaceModelStatus } from '@/types/api/modelDefinition';
import { useSubscriptionPermissions } from '@/hooks/useSubscriptionPermissions/useSubscriptionPermissions';
import type { ModelApprovalStatus } from '@/types/api/modelApproval';
import type { Node } from '@/types/node';

const getAggregateApprovalStatus = (
  importedModelIds: string[],
  statuses: Record<string, ModelApprovalStatus>,
): ModelApprovalStatus => {
  if (importedModelIds.length === 0) return null;
  const values = importedModelIds.map((id) => statuses[id] ?? null);
  if (values.includes(WorkspaceModelStatus.Rejected)) return WorkspaceModelStatus.Rejected;
  if (values.includes(WorkspaceModelStatus.Pending)) return WorkspaceModelStatus.Pending;
  if (values.includes(null)) return null;
  return WorkspaceModelStatus.Approved;
};

export const useApprovalGate = (selectedNodes: Node[]) => {
  const { t } = useTranslation();
  const isCustomModelApprovalEnabled = useIsCustomModelApprovalEnabled();
  const { isWorkspaceAdmin } = useSubscriptionPermissions();
  const modelApprovalStatuses = useWorkflowStore((state) => state.modelApprovalStatuses);
  const isFetching = useIsFetching({ queryKey: [MODEL_APPROVAL_STATUSES_QUERY_KEY] }) > 0;

  const importedModelIds = useMemo(
    () =>
      selectedNodes.flatMap((node) => {
        const { isImportedModel, modelId } = getImportedModelNodeInfo(node.data);
        return isImportedModel && modelId ? [modelId] : [];
      }),
    [selectedNodes],
  );

  const shouldQuery = isCustomModelApprovalEnabled && !isWorkspaceAdmin && importedModelIds.length > 0;

  const workspaceName = useWorkspacesStore((state) => state.activeWorkspace.workspaceName);

  return useMemo(() => {
    if (!shouldQuery) return { approvalDisabled: false, approvalDisabledInfo: undefined };
    if (isFetching) return { approvalDisabled: true, approvalDisabledInfo: undefined };

    const aggregate = getAggregateApprovalStatus(importedModelIds, modelApprovalStatuses);

    if (aggregate === WorkspaceModelStatus.Rejected) {
      return {
        approvalDisabled: true,
        approvalDisabledInfo: t(I18N_KEYS.RECIPE_MAIN.NODES.MODEL_BASE_NODE.IMPORT_APPROVAL.PANEL_CANNOT_RUN_REJECTED, {
          workspaceName,
        }),
      };
    }
    if (aggregate === WorkspaceModelStatus.Pending) {
      return {
        approvalDisabled: true,
        approvalDisabledInfo: t(I18N_KEYS.RECIPE_MAIN.NODES.MODEL_BASE_NODE.IMPORT_APPROVAL.PANEL_CANNOT_RUN_PENDING),
      };
    }
    if (aggregate === null) {
      return {
        approvalDisabled: true,
        approvalDisabledInfo: t(
          I18N_KEYS.RECIPE_MAIN.NODES.MODEL_BASE_NODE.IMPORT_APPROVAL.PANEL_CANNOT_RUN_NOT_REQUESTED,
        ),
      };
    }
    return { approvalDisabled: false, approvalDisabledInfo: undefined };
  }, [shouldQuery, isFetching, modelApprovalStatuses, importedModelIds, workspaceName, t]);
};
